// Mapping of PostgreSQL / PostgREST failure codes onto the HORIZON error envelope
// (services-api, "Referentiel des codes d'erreur"), plus the rule specific user
// messages required by REGLE_VIOLEE.

import type { CodeErreur } from '@horizon/types';

/**
 * SQLSTATE and PostgREST codes observed on the HORIZON schema (migration 0001).
 * Anything absent from this table falls back to ERREUR_INTERNE.
 */
export const CODES_POSTGRES: Readonly<Record<string, CodeErreur>> = {
  // SQLSTATE raised by the schema constraints and triggers.
  '23502': 'SAISIE_INVALIDE', // not_null_violation
  '22P02': 'SAISIE_INVALIDE', // invalid_text_representation
  '22003': 'SAISIE_INVALIDE', // numeric_value_out_of_range
  '22007': 'SAISIE_INVALIDE', // invalid_datetime_format
  '23514': 'REGLE_VIOLEE', // check_violation: RG-04 allocation, RG-10 arbitrage
  '23503': 'REGLE_VIOLEE', // foreign_key_violation
  '23505': 'CONFLIT', // unique_violation: RG-02 unq_simulation_active
  '40001': 'CONFLIT', // serialization_failure
  '40P01': 'CONFLIT', // deadlock_detected
  '42501': 'ACCES_REFUSE', // insufficient_privilege, raised by RLS on write
  '42703': 'ERREUR_INTERNE', // undefined_column, a client side defect
  P0001: 'REGLE_VIOLEE', // raise_exception from the business triggers

  // PostgREST protocol codes.
  PGRST100: 'SAISIE_INVALIDE', // unparsable query string
  PGRST102: 'SAISIE_INVALIDE', // malformed body
  PGRST103: 'SAISIE_INVALIDE', // invalid range
  PGRST106: 'ACCES_REFUSE', // schema not exposed
  PGRST116: 'INTROUVABLE', // single row expected, none returned
  PGRST204: 'SAISIE_INVALIDE', // column not found in the payload
  PGRST301: 'NON_AUTHENTIFIE', // JWT expired or invalid
  PGRST302: 'NON_AUTHENTIFIE', // anonymous access rejected
};

/**
 * Rule identifiers recognised inside a REGLE_VIOLEE message, with the wording
 * shown to the advisor (regles-metier, registre des regles de gestion).
 */
export const MESSAGES_REGLE: Readonly<Record<string, string>> = {
  'RG-02':
    "Ce client possède déjà une simulation active : archivez-la avant d'en activer une autre.",
  'RG-04': 'La répartition doit totaliser exactement 100,00 %.',
  'RG-10': "Une échéance d'arbitrage ne porte pas de montant.",
  'RG-11': "Cette transition d'échéance n'est pas autorisée.",
  'RG-12': 'Seules les occurrences futures encore planifiées peuvent être modifiées.',
  'RG-14': 'Export indisponible : KYC expiré.',
};

/**
 * Resolves the user facing wording of a REGLE_VIOLEE error. The server message is
 * rule specific by contract, so it is kept when no known rule identifier matches.
 */
export function messageRegle(messageServeur: string): string {
  for (const [regle, message] of Object.entries(MESSAGES_REGLE)) {
    if (messageServeur.includes(regle)) {
      return message;
    }
  }
  const nettoye = messageServeur.trim();
  return nettoye.length > 0 ? nettoye : "L'opération est refusée par une règle de gestion.";
}

/** Maps a raw PostgreSQL or PostgREST code onto an envelope code. */
export function codeDepuisPostgres(code: string | undefined): CodeErreur {
  if (code === undefined) {
    return 'ERREUR_INTERNE';
  }
  return CODES_POSTGRES[code] ?? 'ERREUR_INTERNE';
}

/** Maps an HTTP status onto an envelope code (services-api reference table). */
export function codeDepuisStatut(statut: number | undefined): CodeErreur {
  switch (statut) {
    case 400:
      return 'SAISIE_INVALIDE';
    case 401:
      return 'NON_AUTHENTIFIE';
    case 403:
      return 'ACCES_REFUSE';
    case 404:
      return 'INTROUVABLE';
    case 409:
      return 'CONFLIT';
    case 422:
      return 'REGLE_VIOLEE';
    default:
      return 'ERREUR_INTERNE';
  }
}
